import moment from "moment-timezone";

export const formatDuration = (seconds: number, format = 'hh:mm') => {
  if (typeof seconds !== 'number') {
    return '';
  }

  if (seconds < 0) {
    seconds = 0;
  }
  if (format === 'auto') {
    format = seconds < 3600 ? 'm:ss' : 'h:m:ss';
  }

  const h = Math.floor(seconds / 3600);
  const m = Math.floor(seconds % 3600 / 60);
  const s = Math.floor(seconds % 60);

  const hh = `${h < 10 ? '0' : ''}${h}`;
  const mm = `${m < 10 ? '0' : ''}${m}`;
  const ss = `${s < 10 ? '0' : ''}${s}`;

  return format
    .replace('hh', hh)
    .replace('mm', mm)
    .replace('ss', ss)
    .replace('h', h.toString())
    .replace('m', m.toString())
    .replace('s', s.toString());
};

export const fromNow = (date: string | Date, blankText = 'never') => {
  if (!date) {
    return blankText;
  }
  return moment(date).fromNow();
};

export const getTimezone = () => {
  const offset = moment().utcOffset();
  if (!offset) {
    return 'UTC';
  }
  let zone = formatDuration(offset * 60, 'hh:mm');
  if (offset > 0) {
    zone = '+' + zone;
  }
  return zone;
};

// Returns the viewer's current UTC offset as a label, e.g. "UTC+02:00",
// "UTC-05:30", or "UTC" for offset 0. Uses the native Date API directly so
// negative offsets format correctly (the existing getTimezone() helper above
// relies on formatDuration which clamps negative seconds to zero).
export const getUtcOffsetLabel = (): string => {
  const offsetMinutes = -new Date().getTimezoneOffset();
  if (offsetMinutes === 0) return 'UTC';
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `UTC${sign}${hh}:${mm}`;
};

export const formatDate = (dateTimeString: string) => {
  const dateTime = new Date(dateTimeString);
  dateTime.setTime(dateTime.getTime() + (2 * 60 * 60 * 1000));
  const formattedDateTime = dateTime.toISOString().replace(/T/, ' ').replace(/\..+/, '');
  return formattedDateTime;
}

// --- Campaign scheduling helpers (SD-4165) ---------------------------------

/** The viewer's IANA timezone (e.g. "Africa/Johannesburg") — the default for the
 *  scheduled-send timezone selector. */
export const getBrowserTimezone = (): string => moment.tz.guess();

/** Dropdown label for an IANA zone, e.g. "(GMT+02:00) South Africa Standard Time".
 *  Offset + long name are computed for the chosen `refDate` + `refTime` when given,
 *  so a send across a DST boundary shows the offset/name that will actually apply at
 *  the scheduled instant (e.g. New York → EDT -04:00 in July, EST -05:00 in January,
 *  and the correct side of a transition-day). Falls back to the date at midnight,
 *  then to "now", and to the IANA name for an unknown zone. */
export const timezoneLabel = (
  zone: string,
  refDate?: string,
  refTime?: string
): string => {
  const ref =
    refDate && refTime
      ? moment.tz(`${refDate} ${refTime}`, "YYYY-MM-DD HH:mm", zone)
      : refDate
      ? moment.tz(refDate, "YYYY-MM-DD", zone)
      : moment.tz(zone);
  const at = ref.isValid() ? ref : moment.tz(zone);
  const offset = at.format("Z"); // "+02:00"
  let longName = zone;
  try {
    const part = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      timeZoneName: "long",
    })
      .formatToParts(at.toDate())
      .find((p) => p.type === "timeZoneName");
    if (part?.value) longName = part.value;
  } catch {
    /* unknown zone — keep the IANA name */
  }
  return `(GMT${offset}) ${longName}`;
};

/** Combine a local date + time in a chosen zone into the offset-aware ISO 8601
 *  string the backend accepts, e.g.
 *  ("2026-05-05","09:00","Africa/Johannesburg") → "2026-05-05T09:00:00+02:00".
 *  Returns "" for incomplete/invalid input. */
export const toScheduledAtIso = (
  date: string, // "YYYY-MM-DD"
  time: string, // "HH:mm"
  zone: string // IANA name
): string => {
  if (!date || !time || !zone) return "";
  const m = moment.tz(`${date} ${time}`, "YYYY-MM-DD HH:mm", zone);
  // Reject a wall time that does not exist in this zone (the spring-forward DST
  // gap): moment silently shifts e.g. 02:15 → 03:15, which would drift the send off
  // the chosen time. Require the parsed instant to round-trip back to the input.
  if (!m.isValid() || m.format("YYYY-MM-DD HH:mm") !== `${date} ${time}`) return "";
  return m.format();
};

